import { useState, useRef, useEffect } from 'react';
import { ArSdk, isWebGLSupported } from 'tencentcloud-webar';

//const hasWebGL = isWebGLSupported();

/* concept for how user consumes screenshot / photo abilities:
 *  - take an onScreenshotsAvailable prop
 *  - call it with an argument of takeScreenshot() for this filter
 *  - expect it to return a "teardown" callback to run when the takeScreenshot() is no longer valid
 *    to call (e.g. the filter was stopped or had an error)
 * 
 * NB:
 *   Changing authentication keys after initialization/at runtime will have no effect. This is...
 *   SORT OF fine and intended? I don't think they'll ever need to change over the course of one
 *   browser session.
 *     - Empty or wrong authn strings aren't recoverable if they ever make their way into the input props.
 * 
 * If working more on this, may be valuable to hold the SDK in a global variable like 'window.tencentSDK' (it is an
 * external API so that feels somewhat justified) so that we can initialize it and let it fetch auth and load before
 * the user clicks onto the webcam page.  I chose not to spend time on that refactor right now.
 * 
 * Current 'issues':
 * 
 * - I do not know if Tencent counts "usage", for billing purposes, as "this <video> is being played/this output is
 *   being consumed", or if it's just...any time where the Tencent API has been initialized at all. This seems
 *   important to find out?
 *   (update: sdk.stop() and sdk.resume() exist?)
 */

const defaultBeauty = {
  whiten: 0.1,
  dermabrasion: 0.3,
  eye: 0.2,
  chin: 0,
  lift: 0.1,
  shave: 0.2,
};  // really necessary?  idk

function mkAuthFunc(url) {
  return async () => {
    const res = await fetch(url);
    const authdata = await res.json();
    console.log("Tencent lifecycle: got authentication signature ", authdata);
    return authdata;
  };
}

function isValidInput(inpt) {
  const valid = typeof inpt === "string" || inpt instanceof MediaStream;
  if (!valid) {
    if (inpt !== null && inpt !== undefined) {
      console.log("Tencent: Wrong type of input to TencentFilter. This object needs to be a MediaStream: ", inpt);
    }
  }
  return valid;
}

/* We don't have auth parameters that look valid, so we haven't even tried "creating" the SDK. */
const TENCENT_PREINIT = 0;
/* We have created the SDK.  If Tencent's side doesn't give authentication it will hang in this state forever. */
const TENCENT_WAITING_AUTH = 1;
/* The SDK has been created and authenticated successfully.  However, it has not been started with initCore() yet.  If there is no input it will remain in this state until there is input. */
const TENCENT_EXISTS_NO_CORE = 2;
/* The SDK has been created, authenticated, valid input exists, and initCore() has been called.  Input switching can happen here still.  Currently playing */
const TENCENT_PLAYING = 3;
/* The SDK has been created, authenticated, valid input exists, and initCore() has been called.  The SDK is paused */
const TENCENT_PAUSED = 4;
/* The SDK experienced a missing render context error and needs to be resetCore()d.  Unsure if this will ever happen in reality but at least there is *an* attempt to handle it. */
const TENCENT_MISSING_RENDER_CONTEXT_ERROR = 5;

function TencentFilter({ beautyParams = defaultBeauty, appId = null, licenseKey = null, authUrl = null, isLive = true, input, outputVideoRef }) {
  const tencent = useRef(null);
  const [tencentState, setTencentState] = useState(TENCENT_PREINIT);

  // Sanity check:
  if (tencentState !== TENCENT_PREINIT && !tencent.current) {
    console.log("Tencent lifecycle: State is such that SDK should exist, but SDK does not exist.  This should never happen!");
  }

  // Effect that handles the SDK lifecycle and creation.
  useEffect(() => {
    const authValid = typeof appId === "string" && typeof licenseKey === "string" && typeof authUrl === "string";

    if (authValid && tencentState === TENCENT_PREINIT && !tencent.current) {
      console.log("Tencent lifecycle: NEW ARSDK");
      const sdk = new ArSdk({
        auth: {
          appId: appId,
          licenseKey: licenseKey,
          authFunc: mkAuthFunc(authUrl),
        },
        lang: 'en',
        module: {
          beautify: true,
          segmentation: true,
        }
      });
      sdk.on('created', () => {
        console.log('Tencent lifecycle: SDK created');
        // 'For details, see “SDK Integration - Parameters and APIs”.'
        /* These were just being put into a ref and never used. Uncomment and make somewhere to store if you need them
        sdk.getEffectList({
          Type: 'Preset',
          Label: 'Makeup'
        }).then((res) => tencentState.current.sdk_effects = res);
        sdk.getCommonFilter().then(res => tencentState.current.sdk_filters = res); */
        setTencentState(TENCENT_EXISTS_NO_CORE);
      });
      sdk.on('cameraReady', async () => {
        console.log("Tencent lifecycle: SDK cameraReady");
      });
      sdk.on('ready', async () => {
        console.log('Tencent lifecycle: SDK ready');

        /* from previous examples, but not sure what it does yet.  effectList and filterList would be referencing the values from the commented code in on('created')...
        sdk.setEffect({
          id: effectList[0].EffectId,
          intensity: 0.7
        });
        sdk.setFilter(filterList[0].EffectId, 0.5); */

        // In theory, we can rely on the other Effects to do things like setting beauty parameters, pausing, changing input, etcetera because
        // the setTencentState() will fire the relevant ones.
        setTencentState(TENCENT_PLAYING);
      });
      sdk.on('error', error => {
        const { code, message } = error;
        console.log("Tencent error:", code, message);
        // see below
        if (error.code === 10000002) {
          setTencentState(TENCENT_MISSING_RENDER_CONTEXT_ERROR);
        }
      });
      tencent.current = sdk;
      setTencentState(TENCENT_WAITING_AUTH);
    }

    // We want to stop the SDK on component unmount, or when the user navigates away. Most straightforward way to do that
    // is to just use a cleanup function that stops it.
    return () => {
      if (tencent.current !== null && tencent.current !== undefined && typeof tencent.current['stop'] === "function") {
        tencent.current.stop();
      } else {
        console.warn("Tencent lifecycle: unable to call stop() on current SDK: ", tencent.current)
        console.warn("This may be benign as the SDK might not have been created yet.")
      }
    }
  }, [tencentState, appId, licenseKey, authUrl]);

  // Effect that pipes SDK output to the outputVideoRef.
  useEffect(() => {
    if (tencentState === TENCENT_PLAYING) {
      if (typeof outputVideoRef === "object" && outputVideoRef['current'] instanceof HTMLVideoElement) {
        console.log("Tencent lifecycle: Video plumbing attempt with", outputVideoRef);
        const vid_out = outputVideoRef.current;
        vid_out.setAttribute('playsinline', '');
        vid_out.setAttribute('autoplay', '');
        tencent.current.getOutput(4 /*MEDIA_STREAM*/).then(o => vid_out.srcObject = o);
      } else {
        console.error("Tencent lifecycle: No output video ref");
      }
    }
  }, [tencentState, outputVideoRef]);

  // Effect that kicks the SDK when it is in TENCENT_EXISTS_NO_CORE and input is available, or
  // re-kicks it from TENCENT_MISSING_RENDER_CONTEXT_ERROR
  useEffect(() => {
    console.log("Tencent lifecycle: initCore/resetCore effect called; state", tencentState, "input", input);
    if (isValidInput(input) && tencentState === TENCENT_EXISTS_NO_CORE) {
      // TENCENT_{PAUSED, PLAYING} will be set from the ready callback established above
      console.log("Tencent lifecycle: initCore with input", input);
      tencent.current.initCore({
        input: input
      });
      setTencentState(TENCENT_PLAYING);
    } else if (isValidInput(input) && tencentState === TENCENT_MISSING_RENDER_CONTEXT_ERROR) {
      // see https://trtc.io/document/50106?platform=web&product=beautyar under "handling the missing render context error" at bottom of page
      console.log("Tencent lifecycle: resetCore with input", input);
      tencent.current.resetCore(input).then(() => {
        if (isLive) {
          console.log("Tencent lifecycle: resume() from callback of resetCore");
          tencent.current.resume();
          setTencentState(TENCENT_PLAYING);
        } else {
          console.log("Tencent lifecycle: pause() from callback of resetCore");
          tencent.current.pause();
          setTencentState(TENCENT_PAUSED);
        }
      }, () => console.log("Tencent lifecycle: resetCore() happened and promise rejected for some reason"));
    }
  }, [tencentState, input, isLive]);

  // Effect that updates the SDK when the input changes.
  useEffect(() => {
    if (tencentState === TENCENT_PLAYING || tencentState === TENCENT_PAUSED) {
      console.log("Tencent lifecycle: updating input stream to", input);
      if (isValidInput(input)) {
        tencent.current.updateInputStream(input);
      } else {
        tencent.current.stop();
        console.log("Tencent lifecycle: stopped due to null input");
        setTencentState(TENCENT_PAUSED);
      }
    }
  }, [tencentState, input]);

  // Effect that pauses and resumes the SDK when isLive changes.
  useEffect(() => {
    if (tencentState === TENCENT_PAUSED && isLive && isValidInput(input)) {
      console.log("Tencent lifecycle: calling resume()");
      tencent.current.resume();
      setTencentState(TENCENT_PLAYING);
    } else if (tencentState === TENCENT_PLAYING && !isLive) {
      console.log("Tencent lifecycle: calling stop()");
      tencent.current.stop();
    }
  }, [tencentState, isLive, input]);

  // Effect that updates the beauty settings.
  useEffect(() => {
    if (tencentState === TENCENT_PLAYING) {
      console.log("Tencent lifecycle: updating beauty settings", beautyParams);
      tencent.current.setBeautify(beautyParams);
    }
  }, [tencentState, beautyParams]);

  // Duplicated here to make Effect dependencies clearer, as you can't pass this sum of reactive values
  // to an Effect without also depending on it and I'm not sure you're supposed to depend on calculated
  // values of reactive values? Maybe you can though?
  const authValid = typeof appId === "string" && typeof licenseKey === "string" && typeof authUrl === "string";

  // Return a DOM node appropriate to the state.
  if (appId === null || licenseKey === null || authUrl === null) {
    return <span>Waiting for authentication keys</span>
  } else if (!authValid) {
    return <span>Bad/malformed authentication values <code>{JSON.stringify({ key: licenseKey, url: authUrl, appid: appId })}</code></span>
  //} else if (!hasWebGL) {
  //  return <span>Hardware unsupported -- no WebGL</span>
  } else if (tencentState === TENCENT_WAITING_AUTH) {
    // Say something vague so that if users see it they won't be clued in that there's a filter (since the actual experiment text we have doesn't tell them there's a filter)
    return <span>Waiting for camera SDK...</span>
  }

  return <span></span>
}

export default TencentFilter;
